import React, { useEffect, useState } from 'react'
import { createAttendance, fetchAttendance } from '../../../services/AttendanceService'
import { getEmployeeTimeOutWithToken } from '../../../services/EmployeeService'
import LoadingDialog from '../../../helper/LoadingDialog'
import ResponseDialog from '../../../helper/ResponseDialog'
import ConfirmationDialog from '../clock-in-out/ConfirmationDialog'
import QRScannerModal from '../qr-scanner/QRScannerModal'

const Transaction = {
    TIME_IN: 1,
    TIME_OUT: 2
}

const formatTime = (date) =>
    date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })
const formatDate = (date) =>
    date.toLocaleDateString([], { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })

const AttendanceActions = () => {
    const [selectedLocation, setSelectedLocation] = useState('HOME')
    const [currentTime, setCurrentTime] = useState(new Date())
    const [loading, setLoading] = useState(false)
    const [responseMessage, setResponseMessage] = useState('')
    const [responseSeverity, setResponseSeverity] = useState('error')
    const [showResponseDialog, setShowResponseDialog] = useState(false)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [overtimeMessage, setOvertimeMessage] = useState('')
    const [currentTransaction, setCurrentTransaction] = useState(null)
    const [qrCode, setQRCode] = useState(null)
    const [isQrExempted, setIsQrExempted] = useState(false)

    // Current clock status — drives which single action button is shown
    const [isClockedIn, setIsClockedIn] = useState(false)
    const [lastClockTime, setLastClockTime] = useState(null)
    const [statusLoading, setStatusLoading] = useState(true)

    // Offsite details
    const [offsiteLocation, setOffsiteLocation] = useState('')
    const [offsiteTasks, setOffsiteTasks] = useState('')
    const [startTime, setStartTime] = useState('')
    const [endTime, setEndTime] = useState('')

    const locationWorkingAtMap = {
        HOME: 1,
        OFFICE: 2,
        OFFSITE: 3
    }
    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000)

        const checkQRExemption = () => {
            const qrExempt = localStorage.getItem('isQrExempted')
            // Convert to boolean: true if "1", false otherwise
            console.log('isQrExempted', localStorage.getItem('isQRExempted'))
            setIsQrExempted(qrExempt === '1')
            console.log('isQrExempted', qrExempt)
        }
        checkQRExemption()
        return () => clearInterval(timer)
    }, [])

    useEffect(() => {
        const loadTodayStatus = async () => {
            setStatusLoading(true)
            try {
                const now = new Date()
                const dateStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString()
                const dateEnd = new Date(
                    now.getFullYear(),
                    now.getMonth(),
                    now.getDate(),
                    23,
                    59,
                    59,
                    999
                ).toISOString()

                const response = await fetchAttendance({ dateStart, dateEnd })
                const records = response?.dailyTimeRecord || []
                const latest = records[records.length - 1]

                if (latest) {
                    const clockedIn = Boolean(latest.timeIn) && !latest.timeOut
                    setIsClockedIn(clockedIn)
                    setLastClockTime(clockedIn ? latest.timeIn : latest.timeOut || latest.timeIn)
                }
            } catch (error) {
                console.error('Error fetching today status:', error)
            } finally {
                setStatusLoading(false)
            }
        }

        loadTodayStatus()
    }, [])

    const handleOffsiteToggle = (event) => setSelectedLocation(event.target.checked ? 'OFFSITE' : 'HOME')

    const handleClockAction = (transaction) => {
        setCurrentTransaction(transaction)
        console.log('location', locationWorkingAtMap[selectedLocation] === locationWorkingAtMap.OFFSITE)
        if (locationWorkingAtMap[selectedLocation] === locationWorkingAtMap.OFFSITE || isQrExempted) {
            if (transaction === Transaction.TIME_IN) {
                recordAttendance(Transaction.TIME_IN, null)
            } else {
                checkOvertime(null)
            }
        }
    }

    const handleAttendanceWithQR = (qrData) => {
        setQRCode(qrData)
        setLoading(true)

        if (currentTransaction === Transaction.TIME_IN) {
            recordAttendance(Transaction.TIME_IN, qrData)
        } else {
            checkOvertime(qrData)
        }
    }

    const recordAttendance = async (transaction, qrcode, reason = '') => {
        const workingAt = locationWorkingAtMap[selectedLocation] || 0

        if (
            workingAt === locationWorkingAtMap.OFFSITE &&
            (!startTime || !endTime || !offsiteLocation.trim() || !offsiteTasks.trim())
        ) {
            setResponseMessage(
                'Please complete all required offsite details: Start Time, End Time, Location, and Purpose.'
            )
            setResponseSeverity('error')
            setShowResponseDialog(true)
            setLoading(false)
            setCurrentTransaction(null)
            return
        }

        const attendance = {
            transaction,
            workingAt,
            remarks: workingAt === locationWorkingAtMap.OFFSITE ? offsiteTasks : '',
            qrcode,
            isQrExempted: isQrExempted || locationWorkingAtMap[selectedLocation] === locationWorkingAtMap.OFFSITE,
            isOvertime: transaction === Transaction.TIME_OUT && reason !== '',
            reason,
            ...(workingAt === locationWorkingAtMap.OFFSITE && {
                offsiteDetails: {
                    timeStart: startTime,
                    timeEnd: endTime,
                    location: offsiteLocation,
                    purpose: offsiteTasks
                }
            })
        }

        await submitAttendance(attendance)
    }

    const submitAttendance = async (attendance) => {
        setLoading(true)
        try {
            const result = await createAttendance(attendance)
            if (!result.error) {
                setIsClockedIn(attendance.transaction === Transaction.TIME_IN)
                setLastClockTime(new Date().toISOString())
            }
            setResponseSeverity(result.error ? 'error' : 'success')
            setResponseMessage(result.error || result.message)
        } catch (error) {
            console.error('Attendance submission error:', error)
            setResponseSeverity('error')
            setResponseMessage('An error occurred while recording attendance.')
        } finally {
            setLoading(false)
            setShowResponseDialog(true)
            setCurrentTransaction(null)
        }
    }

    const checkOvertime = async (qrData) => {
        try {
            const result = await getEmployeeTimeOutWithToken()
            const timeOut = parseInt(result.timeOut, 10)
            const defaultTimeOut = new Date().setHours(timeOut, 0, 0, 0)
            const gracePeriod = 60 * 60 * 1000 // 1hr

            if (new Date() > defaultTimeOut + gracePeriod) {
                const overtimeDuration = Math.floor((new Date() - defaultTimeOut) / (1000 * 60))
                const overtimeHours = Math.floor(overtimeDuration / 60)
                const overtimeMinutes = overtimeDuration % 60

                setOvertimeMessage(
                    `Please confirm: Do you have ${overtimeHours} hour(s) and ${overtimeMinutes} minute(s) of overtime? If yes, please enter the reason.`
                )
                setIsModalOpen(true)
            } else {
                recordAttendance(Transaction.TIME_OUT, qrData)
            }
        } catch (error) {
            console.error('Overtime check error:', error)
            setResponseSeverity('error')
            setResponseMessage('An error occurred while checking overtime.')
        } finally {
            setLoading(false)
            setCurrentTransaction(null)
        }
    }

    const handleConfirm = (reason) => {
        setIsModalOpen(false)
        recordAttendance(Transaction.TIME_OUT, qrCode, reason)
    }

    const handleCancel = () => {
        setIsModalOpen(false)
        recordAttendance(Transaction.TIME_OUT, qrCode)
    }

    return (
        <div className="card-surface flex flex-col h-full overflow-auto p-6">
            <div className="flex-1 min-w-0">
                {/* Title */}
                <h2 className="text-3xl font-bold text-gray-800 mb-6">Clock In / Out</h2>

                {/* Time Display */}
                <div className="text-center mb-6">
                    <p className="text-xs font-medium text-gray-400 uppercase tracking-widest">
                        {formatDate(currentTime)}
                    </p>
                    <h3 className="text-6xl font-light text-gray-900 tabular-nums mt-2">
                        {formatTime(currentTime)}
                    </h3>
                </div>

                {/* Work Mode Toggle — clocking in from the browser defaults to Home; toggle only for Offsite work */}
                <div className="flex justify-center mb-6">
                    <label className="inline-flex items-center gap-3 cursor-pointer card-surface px-4 py-2">
                        <input
                            type="checkbox"
                            checked={selectedLocation === 'OFFSITE'}
                            onChange={handleOffsiteToggle}
                            className="h-4 w-4 text-red-600 rounded border-gray-300 focus:ring-red-500"
                        />
                        <span className="text-sm font-medium text-gray-700">Working offsite today?</span>
                    </label>
                </div>

                {/* Offsite Details */}
                {selectedLocation === 'OFFSITE' && (
                    <div className="card-surface p-4 mb-6">
                        <h4 className="text-xl font-semibold text-gray-700 mb-4">Offsite Details</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="form-label">Start Time *</label>
                                <input
                                    type="time"
                                    value={startTime}
                                    onChange={(e) => setStartTime(e.target.value)}
                                    className={`input-field ${!startTime ? 'border-red-500' : ''}`}
                                />
                            </div>
                            <div>
                                <label className="form-label">End Time *</label>
                                <input
                                    type="time"
                                    value={endTime}
                                    onChange={(e) => setEndTime(e.target.value)}
                                    className={`input-field ${!endTime ? 'border-red-500' : ''}`}
                                />
                            </div>
                            <div className="md:col-span-2">
                                <label className="form-label">Location *</label>
                                <input
                                    type="text"
                                    value={offsiteLocation}
                                    onChange={(e) => setOffsiteLocation(e.target.value)}
                                    placeholder="e.g., XYZ Corp, Taguig"
                                    className={`input-field ${!offsiteLocation.trim() ? 'border-red-500' : ''}`}
                                />
                            </div>
                            <div className="md:col-span-2">
                                <label className="form-label">Tasks / Purpose *</label>
                                <textarea
                                    value={offsiteTasks}
                                    onChange={(e) => setOffsiteTasks(e.target.value)}
                                    placeholder="e.g., Meeting with client"
                                    rows={3}
                                    className={`input-field ${!offsiteTasks.trim() ? 'border-red-500' : ''}`}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Status */}
                {!statusLoading && (
                    <p className="text-center text-sm text-gray-500 mb-2">
                        {lastClockTime
                            ? isClockedIn
                                ? `Clocked in since ${formatTime(new Date(lastClockTime))}`
                                : `Clocked out at ${formatTime(new Date(lastClockTime))}`
                            : 'Not clocked in yet'}
                    </p>
                )}

                {/* Action Button — single, context-aware: shows the one valid next action */}
                <div className="flex justify-center mt-4 mb-4">
                    <button
                        onClick={() => handleClockAction(isClockedIn ? Transaction.TIME_OUT : Transaction.TIME_IN)}
                        disabled={statusLoading}
                        className="btn-primary text-lg w-full md:w-56"
                    >
                        {isClockedIn ? 'Time Out' : 'Time In'}
                    </button>
                </div>

                {/* QR Exemption Note */}
                {isQrExempted && (
                    <p className="text-center text-green-700 font-medium text-sm mt-2">
                        QR scan is not required for your account.
                    </p>
                )}

                {/* Dialogs */}
                <LoadingDialog loading={loading} />
                <ResponseDialog
                    open={showResponseDialog}
                    onClose={() => setShowResponseDialog(false)}
                    responseMessage={responseMessage}
                    severity={responseSeverity}
                />
                <ConfirmationDialog
                    isOpen={isModalOpen}
                    message={overtimeMessage}
                    onConfirm={handleConfirm}
                    onCancel={handleCancel}
                />
                {locationWorkingAtMap[selectedLocation] !== locationWorkingAtMap.OFFSITE &&
                    !isQrExempted &&
                    currentTransaction && (
                        <QRScannerModal onCancel={() => setCurrentTransaction(null)} onScan={handleAttendanceWithQR} />
                    )}
            </div>
        </div>
    )
}

export default AttendanceActions
